import React, { useEffect, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import otterTeacherImage from '@asset/image/lip-sync/otter-teacher.webp';
import otterWhiteboardBackgroundImage from '@asset/image/lip-sync/otter-whiteboard-background.webp';
import mouthSmallImage from '@asset/image/lip-sync/mouth-small.png';
import mouthMediumImage from '@asset/image/lip-sync/mouth-medium.png';
import mouthLargeImage from '@asset/image/lip-sync/mouth-large.png';
import blinkImage from '@asset/image/lip-sync/blink.png';

interface IMouthSprites {
  small: string;
  medium: string;
  large: string;
}

// board: 칠판 배경 위 16:9 (문법 강의). circle: 배경 없이 상반신만 원형 (AI 말하기)
type TOtterShape = 'board' | 'circle';

interface IProps {
  // 오디오는 상위가 소유하며, 재생 상태만 구독한다. 음량 분석은 하지 않는다.
  audioRef?: React.RefObject<HTMLAudioElement | null>;
  isPlaying?: boolean; // 여러 오디오를 쓰는 화면은 재생 상태를 직접 전달할 수 있다.
  onClick?: () => void; // 수달 클릭(보통 재생/일시정지 토글) — 상위가 처리
  shape?: TOtterShape;
  // circle일 때 원의 지름
  diameter?: string;
}

export interface IPlaybackState {
  currentTime: number;
  duration: number;
  isPlaying: boolean;
  playbackRate: number;
}

const MOUTH_SPRITES: IMouthSprites = {
  small: mouthSmallImage,
  medium: mouthMediumImage,
  large: mouthLargeImage,
};

const bodyMotion = keyframes`
  0%, 100% {
    transform: translateY(0) scale(1);
  }
  24% {
    transform: translateY(-0.12%) scale(1.004, 1.006);
  }
  55% {
    transform: translateY(-0.28%) scale(1.008, 1.014);
  }
  78% {
    transform: translateY(0.08%) scale(1.003, 1.005);
  }
`;

const headMotion = keyframes`
  0%, 100% {
    transform: translate3d(0, 0, 0) rotate(0deg);
  }
  18% {
    transform: translate3d(-0.34%, -0.42%, 0) rotate(-1.05deg);
  }
  43% {
    transform: translate3d(0.2%, 0.18%, 0) rotate(0.62deg);
  }
  67% {
    transform: translate3d(0.38%, -0.3%, 0) rotate(1.12deg);
  }
  84% {
    transform: translate3d(-0.16%, 0.22%, 0) rotate(-0.52deg);
  }
`;

const tailMotion = keyframes`
  0%, 100% {
    transform: rotate(0deg);
  }
  45% {
    transform: rotate(1.8deg);
  }
  75% {
    transform: rotate(-1.15deg);
  }
`;

const blink = keyframes`
  0%, 84%, 92%, 100% {
    opacity: 0;
  }
  86%, 89% {
    opacity: 1;
  }
`;

const Wrapper = styled.div<{ $shape: TOtterShape; $diameter: string }>`
  width: ${({ $shape, $diameter }) => ($shape === 'circle' ? $diameter : 'min(100%, 560px)')};
`;

const Avatar = styled.button<{ $shape: TOtterShape }>`
  position: relative;
  display: block;
  width: 100%;
  padding: 0;
  overflow: hidden;
  box-shadow: none;
  cursor: pointer;

  aspect-ratio: ${({ $shape }) => ($shape === 'circle' ? '1' : '16 / 9')};
  border: ${({ $shape }) => ($shape === 'circle' ? '1px solid #e5e5e5' : '0')};
  border-radius: ${({ $shape }) => ($shape === 'circle' ? '50%' : '0')};
  background-color: ${({ $shape }) => ($shape === 'circle' ? '#fdfaf5' : '#efe3d2')};

  ${({ $shape }) =>
    $shape === 'board' &&
    `
    background-image: url(${otterWhiteboardBackgroundImage});
    background-position: center;
    background-size: cover;
    background-repeat: no-repeat;
  `}
  &:focus-visible {
    outline: none;
    box-shadow: inset 0 0 0 3px rgba(142, 82, 18, 0.42);
  }
`;

// circle은 정사각 원 안에 상반신이 담기도록 확대해 아래로 민다.
const CharacterStage = styled.div<{ $shape: TOtterShape }>`
  position: absolute;
  top: ${({ $shape }) => ($shape === 'circle' ? '60%' : '63%')};
  left: 50%;
  width: ${({ $shape }) => ($shape === 'circle' ? '150%' : '105%')};
  aspect-ratio: 1;
  transform: translate(-50%, -50%);
`;

const CharacterImage = styled.img`
  display: block;
  width: 100%;
  height: auto;
  user-select: none;
  -webkit-user-drag: none;
`;

const MotionLayer = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  will-change: transform;
`;

const LayerImage = styled.img`
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
  user-select: none;
  -webkit-user-drag: none;
`;

const TailRig = styled(MotionLayer)<{ $isPlaying: boolean }>`
  z-index: 1;
  transform-origin: 64% 73%;

  ${(props) =>
    props.$isPlaying &&
    css`
      animation: ${tailMotion} 4.9s ease-in-out infinite;
    `}
`;

const TailLayer = styled(LayerImage)`
  mask-image: radial-gradient(ellipse 15% 9% at 76% 73%, #000 0%, #000 68%, transparent 100%);
  -webkit-mask-image: radial-gradient(ellipse 15% 9% at 76% 73%, #000 0%, #000 68%, transparent 100%);
`;

const BodyRig = styled(MotionLayer)<{ $isPlaying: boolean }>`
  z-index: 2;
  transform-origin: 50% 77%;

  ${(props) =>
    props.$isPlaying &&
    css`
      animation: ${bodyMotion} 3.2s ease-in-out infinite;
    `}
`;

const BodyLayer = styled(LayerImage)`
  mask-image: radial-gradient(ellipse 24% 35% at 50% 63%, #000 0%, #000 72%, transparent 100%);
  -webkit-mask-image: radial-gradient(ellipse 24% 35% at 50% 63%, #000 0%, #000 72%, transparent 100%);
`;

const HeadRig = styled(MotionLayer)<{ $isPlaying: boolean }>`
  z-index: 3;
  transform-origin: 50% 43%;

  ${(props) =>
    props.$isPlaying &&
    css`
      animation: ${headMotion} 5.8s ease-in-out infinite;
    `}
`;

const HeadLayer = styled(LayerImage)`
  mask-image: radial-gradient(ellipse 23% 25% at 50% 32%, #000 0%, #000 72%, transparent 100%);
  -webkit-mask-image: radial-gradient(ellipse 23% 25% at 50% 32%, #000 0%, #000 72%, transparent 100%);
`;

const MouthLayer = styled.img`
  position: absolute;
  top: 42%;
  left: 47.5%;
  width: 5.47%;
  height: auto;
  opacity: 1;
  filter: brightness(1.18) saturate(0.82);
  transform: translate(-50%, -50%);
  pointer-events: none;
  user-select: none;
  -webkit-user-drag: none;
`;

const BlinkLayer = styled(LayerImage)<{
  $isPlaying: boolean;
}>`
  opacity: 0;
  pointer-events: none;

  ${(props) =>
    props.$isPlaying &&
    css`
      animation: ${blink} 4.7s ease-in-out 0.8s infinite;
    `}
`;

// Web Audio 립싱크 복원용 보관 코드. 현재는 오디오 경로를 바꾸지 않는 CSS 애니메이션을 사용한다.
// 복원 시 useRef 및 IS_REACT_NATIVE_APP import, 아래 타입·분석 로직과 입 렌더링을 함께 복원한다.
// type TMouthShape = 'closed' | keyof IMouthSprites;
//
// interface IAudioContextConstructor {
//   new (): AudioContext;
// }
//
// type TAudioContextWindow = Window & {
//   AudioContext?: IAudioContextConstructor;
//   webkitAudioContext?: IAudioContextConstructor;
// };
//
//
// const calculateRms = (samples: Uint8Array) => {
//   const sum = samples.reduce((total, sample) => {
//     const normalized = (sample - 128) / 128;
//     return total + normalized * normalized;
//   }, 0);
//
//   return Math.sqrt(sum / samples.length);
// };
//
// const getMouthShape = (openness: number): TMouthShape => {
//   if (openness < 0.055) return 'closed';
//   if (openness < 0.28) return 'small';
//   if (openness < 0.62) return 'medium';
//   return 'large';
// };
//
// const OtterTeacher: React.FC<IProps> = ({ audioRef, onClick, shape = 'board', diameter = '160px' }) => {
//   const audioContextRef = useRef<AudioContext | null>(null);
//   const analyserRef = useRef<AnalyserNode | null>(null);
//   const sourceRef = useRef<MediaElementAudioSourceNode | null>(null);
//   const animationFrameRef = useRef<number | null>(null);
//   const smoothedOpennessRef = useRef(0);
//   const mouthShapeRef = useRef<TMouthShape>('closed');
//   const [isPlaying, setIsPlaying] = useState(false);
//   const [isAudioSupported, setIsAudioSupported] = useState(true);
//   const [mouthShape, setMouthShape] = useState<TMouthShape>('closed');
//
//   const updateMouthShape = (nextShape: TMouthShape) => {
//     if (mouthShapeRef.current === nextShape) return;
//     mouthShapeRef.current = nextShape;
//     setMouthShape(nextShape);
//   };
//
//   const stopLipSync = () => {
//     if (animationFrameRef.current !== null) {
//       cancelAnimationFrame(animationFrameRef.current);
//       animationFrameRef.current = null;
//     }
//
//     smoothedOpennessRef.current = 0;
//     updateMouthShape('closed');
//   };
//
//   const startLipSync = () => {
//     const analyser = analyserRef.current;
//     if (!analyser) return;
//
//     const samples = new Uint8Array(analyser.fftSize);
//
//     const animate = () => {
//       analyser.getByteTimeDomainData(samples);
//       const rms = calculateRms(samples);
//       const targetOpenness = Math.min(Math.max((rms - 0.018) * 8, 0), 1);
//       smoothedOpennessRef.current = smoothedOpennessRef.current * 0.68 + targetOpenness * 0.32;
//       updateMouthShape(getMouthShape(smoothedOpennessRef.current));
//       animationFrameRef.current = requestAnimationFrame(animate);
//     };
//
//     animate();
//   };
//
//   const initializeAudio = async () => {
//     const audio = audioRef?.current;
//     if (!audio) return false;
//
//     const audioWindow = window as TAudioContextWindow;
//     const AudioContextConstructor = audioWindow.AudioContext ?? audioWindow.webkitAudioContext;
//     if (!AudioContextConstructor) {
//       setIsAudioSupported(false);
//       return false;
//     }
//
//     try {
//       let audioContext = audioContextRef.current;
//       if (!audioContext) {
//         audioContext = new AudioContextConstructor();
//         const analyser = audioContext.createAnalyser();
//         const source = audioContext.createMediaElementSource(audio);
//         analyser.fftSize = 256;
//         analyser.smoothingTimeConstant = 0.72;
//         source.connect(analyser);
//         analyser.connect(audioContext.destination);
//         audioContextRef.current = audioContext;
//         analyserRef.current = analyser;
//         sourceRef.current = source;
//       }
//
//       await audioContext.resume();
//       return true;
//     } catch {
//       setIsAudioSupported(false);
//       return false;
//     }
//   };
//
//   // 주입된 audio의 재생 상태를 구독해 립싱크·애니메이션을 제어한다(audio 소유·제어는 상위 플레이어).
//   useEffect(() => {
//     const audio = audioRef?.current;
//     if (!audio) return;
//
//     const handlePlay = async () => {
//       setIsPlaying(true);
//       stopLipSync();
//
//       // 모바일 WebView에서는 MediaElementSource를 거치면 HTMLAudioElement의
//       // playbackRate가 불안정해질 수 있어 음량 기반 립싱크 분석을 생략한다.
//       // 오디오는 기본 미디어 출력 경로를 사용하고, 수달의 몸·눈 애니메이션은 유지한다.
//       if (IS_REACT_NATIVE_APP) return;
//
//       const initialized = await initializeAudio();
//       if (!initialized) {
//         setIsPlaying(false);
//         return;
//       }
//       startLipSync();
//     };
//
//     const handlePause = () => {
//       setIsPlaying(false);
//       stopLipSync();
//     };
//
//     audio.addEventListener('play', handlePlay);
//     audio.addEventListener('pause', handlePause);
//     audio.addEventListener('ended', handlePause);
//
//     return () => {
//       audio.removeEventListener('play', handlePlay);
//       audio.removeEventListener('pause', handlePause);
//       audio.removeEventListener('ended', handlePause);
//     };
//   }, [audioRef]);
//
//   useEffect(() => {
//     return () => {
//       stopLipSync();
//       audioContextRef.current?.close();
//     };
//   }, []);
//             {mouthShape !== 'closed' && (
//               <MouthLayer
//                 src={MOUTH_SPRITES[mouthShape]}
//                 alt=''
//                 draggable={false}
//                 data-testid='mouth-layer'
//                 data-mouth-shape={mouthShape}
//               />
//             )}
const mouthMotion = keyframes`
  0%, 24% { opacity: 1; }
  25%, 100% { opacity: 0; }
`;

const AnimatedMouthLayer = styled(MouthLayer)<{ $delay: number }>`
  opacity: 0;
  animation: ${mouthMotion} 0.72s steps(1, end) ${(props) => props.$delay}s infinite;
`;

const OtterTeacher: React.FC<IProps> = ({
  audioRef, isPlaying: externalIsPlaying, onClick, shape = 'board', diameter = '160px',
}) => {
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const isPlaying = externalIsPlaying ?? isAudioPlaying;

  // 오디오 출력 경로는 바꾸지 않고 실제 재생·정지 이벤트로 애니메이션만 전환한다.
  useEffect(() => {
    const audio = audioRef?.current;
    if (!audio) return;

    const handlePlaying = () => setIsAudioPlaying(true);
    const handleStopped = () => setIsAudioPlaying(false);
    setIsAudioPlaying(!audio.paused && !audio.ended && audio.readyState >= 3);
    audio.addEventListener('playing', handlePlaying);
    audio.addEventListener('pause', handleStopped);
    audio.addEventListener('ended', handleStopped);
    audio.addEventListener('waiting', handleStopped);
    audio.addEventListener('emptied', handleStopped);
    audio.addEventListener('error', handleStopped);

    return () => {
      audio.removeEventListener('playing', handlePlaying);
      audio.removeEventListener('pause', handleStopped);
      audio.removeEventListener('ended', handleStopped);
      audio.removeEventListener('waiting', handleStopped);
      audio.removeEventListener('emptied', handleStopped);
      audio.removeEventListener('error', handleStopped);
    };
  }, [audioRef]);

  return (
    <Wrapper $shape={shape} $diameter={diameter}>
      <Avatar
        type='button'
        $shape={shape}
        onClick={onClick}
        aria-label={isPlaying ? '문법을 설명하는 수달 선생님 음성 일시정지' : '문법을 설명하는 수달 선생님 음성 재생'}>
        <CharacterStage $shape={shape}>
          <CharacterImage src={otterTeacherImage} alt='문법을 설명하는 수달 선생님' draggable={false} />
          <TailRig $isPlaying={isPlaying} aria-hidden='true'>
            <TailLayer src={otterTeacherImage} alt='' draggable={false} />
          </TailRig>
          <BodyRig $isPlaying={isPlaying} aria-hidden='true'>
            <BodyLayer src={otterTeacherImage} alt='' draggable={false} />
          </BodyRig>
          <HeadRig $isPlaying={isPlaying} aria-hidden='true' data-testid='head-rig'>
            <HeadLayer src={otterTeacherImage} alt='' draggable={false} />
            <BlinkLayer src={blinkImage} alt='' draggable={false} $isPlaying={isPlaying} />
            {isPlaying && (
              <>
                <AnimatedMouthLayer src={MOUTH_SPRITES.small} alt='' draggable={false} $delay={0} />
                <AnimatedMouthLayer src={MOUTH_SPRITES.medium} alt='' draggable={false} $delay={-0.54} />
                <AnimatedMouthLayer src={MOUTH_SPRITES.large} alt='' draggable={false} $delay={-0.36} />
              </>
            )}
          </HeadRig>
        </CharacterStage>
      </Avatar>
    </Wrapper>
  );
};

export default OtterTeacher;
